import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Logo from '../../assets/images/logo.svg'
import LogoDark from '../../assets/images/logo-dark.svg'
import Button from '../Button/Button'
import useIsMobile from '../../hooks/useIsMobile'
import { BOOKING_URL, NAV_LINKS } from '../../constants/site'
import { cn } from '../../utils/classNames'

const desktopNavLinkBase =
  "relative inline-block text-sm font-semibold no-underline after:absolute after:bottom-0.5 after:left-1/2 after:h-0.5 after:w-4/5 after:-translate-x-1/2 after:bg-blue-dark after:opacity-0 after:transition-opacity after:content-[''] hover:after:opacity-100"

const mobileNavLinkBase =
  "relative my-2.5 inline-block text-lg font-semibold text-white no-underline after:absolute after:bottom-0 after:left-1/2 after:h-[3px] after:w-full after:-translate-x-1/2 after:bg-white after:opacity-0 after:transition-opacity after:content-[''] hover:text-white hover:after:opacity-100"

function Nav() {
  const { pathname } = useLocation()
  const isHomePage = pathname === '/'
  const isMobile = useIsMobile()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    setIsMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false)
    }
  }, [isMenuOpen, isMobile])

  const toggleMenu = () => {
    setIsMenuOpen((currentIsOpen) => !currentIsOpen)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
  }

  const getDesktopNavClass = (path) =>
    cn(
      desktopNavLinkBase,
      isHomePage ? 'text-white hover:text-blue-dark' : 'text-blue-dark hover:text-blue-dark',
      path !== '/' && pathname === path && 'after:opacity-100'
    )

  const getMobileNavClass = (path) => cn(mobileNavLinkBase, pathname === path && 'after:opacity-100')

  return (
    <div
      className={cn(
        isHomePage && 'relative h-[600px] w-full overflow-hidden bg-main-wave bg-cover bg-center bg-no-repeat'
      )}
    >
      <nav className='container mb-12 flex items-center justify-between py-2'>
        <Link className='inline-flex items-center' to='/'>
          <img src={isHomePage ? Logo : LogoDark} className='h-[60px]' alt='Logo' />
        </Link>

        <button
          className='inline-flex h-10 w-12 items-center justify-center rounded border border-black/10 bg-transparent lg:hidden'
          type='button'
          aria-controls='siteNavigation'
          aria-expanded={isMenuOpen}
          aria-label='Toggle navigation'
          onClick={toggleMenu}
        >
          <span className='flex w-6 flex-col gap-1.5'>
            <span className='h-0.5 rounded bg-blue-dark'></span>
            <span className='h-0.5 rounded bg-blue-dark'></span>
            <span className='h-0.5 rounded bg-blue-dark'></span>
          </span>
        </button>

        <div className='hidden items-center lg:flex' id='siteNavigation'>
          <ul className='flex items-center gap-6'>
            {NAV_LINKS.map((link) => (
              <li key={link.path}>
                <Link className={getDesktopNavClass(link.path)} to={link.path}>
                  {link.label}
                </Link>
              </li>
            ))}
            <li>
              <Button
                label='BOOK NOW'
                navigateExternal={BOOKING_URL}
                buttonType={isHomePage ? 'primary-light' : 'secondary-dark'}
              />
            </li>
          </ul>
        </div>
      </nav>

      <div
        className={cn(
          'fixed inset-0 z-[999] flex-col items-start justify-start bg-brand-gradient-nav px-11 pt-8',
          isMenuOpen ? 'flex' : 'hidden'
        )}
        onClick={closeMenu}
      >
        <ul className='flex w-full flex-col items-start'>
          {NAV_LINKS.map((link) => (
            <li key={link.path}>
              <Link className={getMobileNavClass(link.path)} to={link.path}>
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {isHomePage && (
        <>
          <div className='container flex h-[430px] items-center'>
            <div className='grid w-full grid-cols-1 md:grid-cols-2'>
              <div className='flex flex-col justify-center text-white'>
                <h1 className='mb-6 font-serif text-[32px] font-bold leading-tight md:text-5xl'>
                  Nurturing wellness, one adjustment at a time
                </h1>
                <p className='mb-10'>
                  We are registered primary healthcare practitioners dedicated to restoring health and balance
                  through holistic methods. Trained in diagnosing and treating musculoskeletal injuries, we
                  utilise a variety of osteopathic techniques.
                </p>
                <Button label='BOOK NOW' buttonType='primary-light btn-127w' navigateExternal={BOOKING_URL} />
              </div>
              <div className='hidden md:block'></div>
            </div>
          </div>
          <svg
            className='pointer-events-none absolute bottom-0 left-0 h-[150px] w-full'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 1440 320'
            preserveAspectRatio='none'
          >
            <path
              fill='white'
              fillOpacity='1'
              d='M0,224 C288,282,576,166,864,212 C1152,258,1440,194,1728,224 L1728,320 L0,320 Z'
            ></path>
            <path
              fill='white'
              fillOpacity='0.15'
              d='M0,96 C288,32,576,256,864,192 C1152,128,1440,256,1728,192 L1728,320 L0,320 Z'
            ></path>
            <path
              fill='white'
              fillOpacity='0.5'
              d='M0,160 C288,224,576,128,864,192 C1152,256,1440,192,1728,160 L1728,320 L0,320 Z'
            ></path>
          </svg>
        </>
      )}
    </div>
  )
}

export default Nav
